// src/components/SEO.jsx
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const BASE_URL = "https://studio.wiyoutech.in";
const DEFAULT_TITLE = "WiYou! Tech Studios | Web Development & Design Company in Jaipur";
const DEFAULT_DESCRIPTION =
  "WiYou! Tech Studios is a Jaipur-based digital technology studio creating modern websites, web applications, custom software, digital menus, and QR menu solutions for businesses across India.";
const DEFAULT_IMAGE = `${BASE_URL}/assets/logo.png`;

export default function SEO({
  title,
  description,
  keywords,
  canonicalUrl,
  ogType = "website",
  ogImage,
  structuredData,
}) {
  const location = useLocation();

  useEffect(() => {
    const finalTitle = title ? `${title}` : DEFAULT_TITLE;
    const finalDesc = description || DEFAULT_DESCRIPTION;
    const finalCanonical = canonicalUrl || `${BASE_URL}${location.pathname}`;
    const finalImage = ogImage || DEFAULT_IMAGE;

    // 1. Title
    document.title = finalTitle;

    // Helper to update or create meta tags
    const setMeta = (attr, key, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    // 2. Primary Meta Tags
    setMeta("name", "description", finalDesc);
    if (keywords) setMeta("name", "keywords", keywords);

    // 3. Canonical Tag
    let canonicalTag = document.querySelector('link[rel="canonical"]');
    if (!canonicalTag) {
      canonicalTag = document.createElement("link");
      canonicalTag.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalTag);
    }
    canonicalTag.setAttribute("href", finalCanonical);

    // 4. Open Graph Tags
    setMeta("property", "og:title", finalTitle);
    setMeta("property", "og:description", finalDesc);
    setMeta("property", "og:url", finalCanonical);
    setMeta("property", "og:type", ogType);
    setMeta("property", "og:image", finalImage);
    setMeta("property", "og:site_name", "WiYou! Tech Studios");

    // 5. Twitter Card Tags
    setMeta("name", "twitter:title", finalTitle);
    setMeta("name", "twitter:description", finalDesc);
    setMeta("name", "twitter:image", finalImage);
    setMeta("name", "twitter:card", "summary_large_image");

    // 6. JSON-LD Structured Data
    if (structuredData) {
      let scriptTag = document.getElementById("seo-page-schema");
      if (!scriptTag) {
        scriptTag = document.createElement("script");
        scriptTag.id = "seo-page-schema";
        scriptTag.type = "application/ld+json";
        document.head.appendChild(scriptTag);
      }
      scriptTag.textContent = JSON.stringify(structuredData);
    } else {
      const existingScript = document.getElementById("seo-page-schema");
      if (existingScript) existingScript.remove();
    }
  }, [title, description, keywords, canonicalUrl, ogType, ogImage, structuredData, location.pathname]);

  return null;
}
